// Motion on this device (docs/plans/app-motion.md): the person's own "Full" or "Reduced",
// kept in localStorage like the theme, and the kind of page move about to happen, which
// the page-change animation in packages/ui/src/styles.css reads from <html data-move>.
import { useCallback, useSyncExternalStore, type MouseEvent } from 'react'
import { useNavigate, type To } from 'react-router'

export type MotionChoice = 'full' | 'reduced'
const KEY = 'ims.motion'
const listeners = new Set<() => void>()

function read(): MotionChoice {
  try {
    return localStorage.getItem(KEY) === 'reduced' ? 'reduced' : 'full'
  } catch {
    return 'full'
  }
}

let current: MotionChoice = read()

function apply(choice: MotionChoice) {
  if (choice === 'reduced') document.documentElement.dataset.motion = 'reduce'
  else delete document.documentElement.dataset.motion
}

/** Before the first render: the saved choice, and Back slides the right way on a phone. */
export function initMotion() {
  apply(current)
  // The router turns a Back into a page change when the forward move had one; mark it as
  // a move back before the browser takes its picture of the old page.
  window.addEventListener('popstate', () => setMove('back'), { capture: true })
}

export function setMotion(choice: MotionChoice) {
  current = choice
  try {
    if (choice === 'full') localStorage.removeItem(KEY)
    else localStorage.setItem(KEY, choice)
  } catch {
    // Storage blocked: the choice still applies for this session.
  }
  apply(choice)
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useMotion(): [MotionChoice, (choice: MotionChoice) => void] {
  return [useSyncExternalStore(subscribe, () => current), setMotion]
}

/** What the next page change is: another section (a fade), or an item opened (a slide on a phone). */
export function setMove(kind: 'section' | 'open' | 'back') {
  document.documentElement.dataset.move = kind
}

// The app's one router (App.tsx). Links under the screens' <Routes> navigate through
// history, which cannot animate, so animated moves go through the router itself; it also
// remembers them, so Back from there animates too.
let router: {
  navigate: (to: To, opts?: { viewTransition?: boolean; flushSync?: boolean }) => Promise<void>
} | null = null

export function setRouter(made: typeof router) {
  router = made
}

/** Go to `to` as a page change of this kind (the router animates it where it can). */
export function moveTo(to: To, kind: 'section' | 'open') {
  setMove(kind)
  // flushSync: the new page is drawn inside the transition's own callback, in one go.
  if (router) void router.navigate(to, { viewTransition: true, flushSync: true })
}

/**
 * A link's click as a page change. A click with a modifier key (a new tab) or one already
 * handled keeps the browser's own behaviour.
 */
export function moveClick(to: To, kind: 'section' | 'open') {
  return (event: MouseEvent<HTMLElement>) => {
    if (!router) return
    if (event.defaultPrevented || event.button !== 0) return
    if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) return
    event.preventDefault()
    moveTo(to, kind)
  }
}

/** Opens one item from a list (a row's click), sliding it in on a phone. */
export function useOpen() {
  const navigate = useNavigate()
  return useCallback(
    (to: To) => {
      if (router) moveTo(to, 'open')
      else void navigate(to)
    },
    [navigate],
  )
}

/**
 * True the first time it is asked on a given day on this device (then false until
 * tomorrow): the dashboard's chart grows once a day, not on every visit.
 */
export function firstTimeToday(key: string): boolean {
  const today = new Date().toISOString().slice(0, 10)
  try {
    const storageKey = `ims.once.${key}`
    if (localStorage.getItem(storageKey) === today) return false
    localStorage.setItem(storageKey, today)
    return true
  } catch {
    return false
  }
}
